import { Location } from "@angular/common";
import { Component, Input, OnInit, ViewEncapsulation } from "@angular/core";

import { ActivatedRoute } from "@angular/router";
import { AgCartesianSeriesTooltipRendererParams } from "ag-charts-community";
import { ColDef } from "@ag-grid-community/core";
import { Account } from "app/model/entities/entity-model";

import { JoStatusCode } from "app/model/enums/jo-status-code";
import { DateFns } from "app/shared/date-fns";
import { DbQueryService } from "app/shared/db-query.service";
import { DialogBaseComponent } from "app/shared/dialog-base.component";
import { DomainBaseComponent } from "app/shared/domain-base.component";
import { DomainService } from "app/shared/domain.service";
import _ from "lodash";
import * as ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { JoStatsDialogComponent } from "./jo-stats-dialog.component";

interface JoStatsData {
  monthStart: string;
  incomplete: number;
  complete: number;
  sortKey: Date;
}

@Component({
  selector: "app-chart-jo-stats",
  templateUrl: "./chart-jo-stats.component.html",

  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class ChartJoStatsComponent
  extends DomainBaseComponent
  implements OnInit
{
  @Input() account: Account;
  /** Accounts set to Exclude from Dashboard Analytics that this view puts back in. */
  @Input() includedAccountIds: number[] = [];
  title: string;
  options: any;
  private chartData: JoStatsData[] = [];

  constructor(
    protected domainService: DomainService,
    protected route: ActivatedRoute,

    protected location: Location,
    protected dbQueryService: DbQueryService
  ) {
    super(domainService);
  }

  async ngOnInit() {
    await this.processChartData();
    this.isPageReady = true;
  }

  //this will called when data is passed from parent to child.
  async ngOnChanges(val) {
    this.isPageReady = false;
    await this.processChartData();
    this.isPageReady = true;
  }

  private async processChartData() {
    // this.title = this.getTitle();
    const data = await this.dbQueryService.getJobOrdersByStatusForChart(this.includedAccountIds);
    DateFns.fixupDatesInProjection(data, ["monthStart"]);

    // reformat data for chart
    const oData = {};
    data.forEach((d) => {
      const fDate = this.formatDateAsMonth(d.monthStart);
      let cell = oData[fDate];
      if (cell == null) {
        cell = {
          monthStart: fDate,
          incomplete: 0,
          complete: 0,
          sortKey: d.monthStart,
        };
        oData[fDate] = cell;
      }
      const key =
        d.joStatusId == JoStatusCode.OpenProcessComplete
          ? "complete"
          : "incomplete";
      cell[key] = d.count;
    });
    let rData = Object.values(oData) as JoStatsData[];
    rData = _.sortBy(rData, (x: any) => x.sortKey);
    this.chartData = rData;

    this.options = {
      title: {
        text: this.getTitle(),
      },
      data: rData,

      series: [
        {
          type: "bar",
          xKey: "monthStart",
          yKey: "incomplete",
          yName: "Incomplete",
          tooltip: { renderer: this.renderer.bind(this) },
          listeners: {
            nodeClick: async (event: any) => {
              const joStatusId =
                event.yKey == "incomplete"
                  ? JoStatusCode.OpenProcessIncomplete
                  : JoStatusCode.OpenProcessComplete;
              const data = await DialogBaseComponent.open(
                JoStatsDialogComponent,
                this,
                {
                  joStatusId: joStatusId,
                  dateInMonth: event.datum.sortKey,
                }
              );
            },
          },
        },
        {
          type: "bar",
          xKey: "monthStart",
          yKey: "complete",
          yName: "Complete",
          tooltip: { renderer: this.renderer.bind(this) },
          listeners: {
            nodeClick: async (event: any) => {
              const joStatusId =
                event.yKey == "incomplete"
                  ? JoStatusCode.OpenProcessIncomplete
                  : JoStatusCode.OpenProcessComplete;
              const data = await DialogBaseComponent.open(
                JoStatsDialogComponent,
                this,
                {
                  joStatusId: joStatusId,
                  dateInMonth: event.datum.sortKey,
                }
              );
            },
          },
        },
      ],
    };
  }

  getTitle() {
    return `Current Job Orders by Age and Status`;
  }

  renderer(params: AgCartesianSeriesTooltipRendererParams) {
    return {
      title: params.yName,
      content:
        "Created on " +
        params.datum.monthStart +
        ": " +
        params.yName +
        "<br><b>Click to see details</b>",
    };
  }

  formatDateAsMonth(date: Date) {
    const options = { year: "numeric", month: "long" } as any;
    return new Intl.DateTimeFormat("en-US", options).format(date);
  }

  // ViewableWidget interface methods
  async exportToExcel(): Promise<void> {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("JO Stats");
    const exportDate = new Date();

    worksheet.addRow([this.getTitle()]);
    worksheet.getRow(1).font = { bold: true, size: 14 };
    worksheet.mergeCells("A1:C1");

    worksheet.addRow([`Exported: ${exportDate.toLocaleDateString()} ${exportDate.toLocaleTimeString()}`]);
    worksheet.getRow(2).font = { italic: true, size: 10 };
    worksheet.mergeCells("A2:C2");

    worksheet.addRow([]);

    worksheet.addRow(["Month", "Incomplete", "Complete"]);
    const headerRow = worksheet.getRow(4);
    headerRow.font = { bold: true };
    headerRow.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFE0E0E0" } };

    worksheet.getColumn(1).width = 20;
    worksheet.getColumn(2).width = 15;
    worksheet.getColumn(3).width = 15;

    this.chartData.forEach((item) => {
      worksheet.addRow([item.monthStart, item.incomplete, item.complete]);
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
    saveAs(blob, `JO_Stats_${exportDate.toISOString().split("T")[0]}.xlsx`);
  }

  getGridData(): JoStatsData[] {
    return this.chartData;
  }

  getColumnDefs(): ColDef[] {
    return [
      { field: "monthStart", headerName: "Month", flex: 2 },
      { field: "incomplete", headerName: "Incomplete", flex: 1 },
      { field: "complete", headerName: "Complete", flex: 1 },
    ];
  }

  getExportFileName(): string {
    return "JO_Stats";
  }

  getDialogTitle(): string {
    return this.getTitle();
  }
}
